import {useEffect, useState} from 'react'
import {applyServiceWorkerUpdate} from '../pwaRegistration'

const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true

const getInstallContext = () => {
    const userAgent = window.navigator.userAgent || ''
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) ||
        (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
    const isAndroid = /Android/i.test(userAgent)
    const isMobile = isIOS || isAndroid || /Mobile/i.test(userAgent)
    const isInAppBrowser = /(FBAN|FBAV|Instagram|Line|Twitter|TikTok|Snapchat|LinkedInApp|WhatsApp)/i.test(userAgent)

    if (isStandalone() || !isMobile) return null
    if (isIOS && isInAppBrowser) return 'ios-in-app'
    if (isIOS) return 'ios'
    if (isAndroid && isInAppBrowser) return 'android-in-app'
    if (isAndroid) return 'android'
    return 'mobile'
}

const getInstallText = context => {
    if (context === 'ios' || context === 'ios-in-app') return 'تثبيت على iPhone'
    if (context === 'android' || context === 'android-in-app') return 'تثبيت على Android'
    return 'تثبيت التطبيق'
}

const PWAStatus = () => {
    const [isOnline, setIsOnline] = useState(() => navigator.onLine)
    const [deferredPrompt, setDeferredPrompt] = useState(null)
    const [updateRegistration, setUpdateRegistration] = useState(null)
    const [showInstall, setShowInstall] = useState(false)
    const [installContext, setInstallContext] = useState(null)
    const [showGuide, setShowGuide] = useState(false)
    const [isDismissed, setIsDismissed] = useState(() => sessionStorage.getItem('pwa-install-dismissed') === 'true')

    useEffect(() => {
        setInstallContext(getInstallContext())

        const onlineHandler = () => setIsOnline(true)
        const offlineHandler = () => setIsOnline(false)
        const installPromptHandler = event => {
            event.preventDefault()
            setDeferredPrompt(event)
            setShowInstall(true)
        }
        const installedHandler = () => {
            setDeferredPrompt(null)
            setShowInstall(false)
            setInstallContext(null)
        }
        const updateHandler = event => {
            setUpdateRegistration(event.detail.registration)
        }

        window.addEventListener('online', onlineHandler)
        window.addEventListener('offline', offlineHandler)
        window.addEventListener('beforeinstallprompt', installPromptHandler)
        window.addEventListener('appinstalled', installedHandler)
        window.addEventListener('pwa-update-available', updateHandler)

        return () => {
            window.removeEventListener('online', onlineHandler)
            window.removeEventListener('offline', offlineHandler)
            window.removeEventListener('beforeinstallprompt', installPromptHandler)
            window.removeEventListener('appinstalled', installedHandler)
            window.removeEventListener('pwa-update-available', updateHandler)
        }
    }, [])

    const installHandler = async () => {
        if (!deferredPrompt) return
        deferredPrompt.prompt()
        await deferredPrompt.userChoice
        setDeferredPrompt(null)
        setShowInstall(false)
        setInstallContext(null)
    }

    const installGuideHandler = () => setShowGuide(true)

    const dismissInstallHandler = () => {
        sessionStorage.setItem('pwa-install-dismissed', 'true')
        setIsDismissed(true)
    }

    const updateHandler = () => {
        applyServiceWorkerUpdate(updateRegistration)
        setUpdateRegistration(null)
    }

    const shouldShowInstall = !isDismissed && (showInstall || installContext)

    if (isOnline && !shouldShowInstall && !updateRegistration) return null

    return (
        <>
            <div className='pwa-status' role='status' aria-live='polite'>
                {!isOnline && (
                    <div className='pwa-status__message'>
                        <i className='fas fa-wifi'></i>
                        <span>أنت تعمل بدون اتصال. سنستخدم آخر بيانات محفوظة عند توفرها.</span>
                    </div>
                )}

                {updateRegistration && (
                    <button className='pwa-status__action' type='button' onClick={updateHandler}>
                        تحديث التطبيق
                    </button>
                )}

                {showInstall && (
                    <button className='pwa-status__action' type='button' onClick={installHandler}>
                        تثبيت التطبيق
                    </button>
                )}

                {!showInstall && installContext && !isDismissed && (
                    <button className='pwa-status__action' type='button' onClick={installGuideHandler}>
                        {getInstallText(installContext)}
                    </button>
                )}

                {shouldShowInstall && (
                    <button className='pwa-status__dismiss' type='button' aria-label='إخفاء رسالة التثبيت' onClick={dismissInstallHandler}>
                        <i className='fas fa-times'></i>
                    </button>
                )}
            </div>

            {showGuide && (
                <div className='pwa-guide' role='dialog' aria-modal='true' aria-labelledby='pwa-guide-title'>
                    <div className='pwa-guide__content'>
                        <button className='pwa-guide__close' type='button' onClick={() => setShowGuide(false)}>
                            <i className='fas fa-times'></i>
                        </button>
                        <h2 id='pwa-guide-title'>{getInstallText(installContext)}</h2>
                        {(installContext === 'ios' || installContext === 'ios-in-app') ? (
                            <>
                                {installContext === 'ios-in-app' && (
                                    <p className='pwa-guide__notice'>افتح الرابط أولاً في Safari، لأن المتصفح الداخلي لا يدعم التثبيت الكامل.</p>
                                )}
                                <ol>
                                    <li>افتح الموقع من Safari.</li>
                                    <li>اضغط زر المشاركة أسفل الشاشة.</li>
                                    <li>اختر إضافة إلى الشاشة الرئيسية.</li>
                                    <li>اضغط إضافة، ثم افتح التطبيق من الأيقونة الجديدة.</li>
                                </ol>
                            </>
                        ) : installContext === 'android-in-app' ? (
                            <>
                                <p className='pwa-guide__notice'>افتح الرابط في Chrome للحصول على زر التثبيت الرسمي.</p>
                                <ol>
                                    <li>افتح قائمة المتصفح الداخلي.</li>
                                    <li>اختر فتح في Chrome إن وُجدت.</li>
                                    <li>اضغط تثبيت التطبيق من الرسالة أو قائمة Chrome.</li>
                                </ol>
                            </>
                        ) : installContext === 'android' ? (
                            <ol>
                                <li>اضغط تثبيت التطبيق إذا ظهرت الرسالة الرسمية.</li>
                                <li>إذا لم تظهر، افتح قائمة Chrome.</li>
                                <li>اختر تثبيت التطبيق أو إضافة إلى الشاشة الرئيسية.</li>
                            </ol>
                        ) : (
                            <ol>
                                <li>افتح الموقع من Chrome أو المتصفح الرئيسي.</li>
                                <li>افتح قائمة المتصفح.</li>
                                <li>اختر تثبيت التطبيق أو إضافة إلى الشاشة الرئيسية.</li>
                            </ol>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default PWAStatus
